// build-ts-1789355114
import { useState } from 'react';
import { CheckCircle2, Phone, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { COMPANY, FENCE_TYPES, TIMELINES } from '../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', city: '',
    service: '', timeline: '', length: '', notes: '',
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error('Please add your name and phone number so we can respond.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSent(true);
      toast.success('Thanks! We\u2019ve got your request and will be in touch shortly.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please call us directly.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-24 lg:py-32 bg-ink-soft border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #d4a13a 0%, transparent 45%)' }} />

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-12 items-start relative">
        <div className="lg:col-span-2">
          <div className="eyebrow">Request a Quote</div>
          <h2 className="section-title">Get your free fencing quote.</h2>
          <p className="section-lede">
            Fill out the form and we&rsquo;ll come out, measure, and hand you a detailed, no-obligation written estimate.
          </p>

          <div className="mt-8 space-y-4">
            {['Fast Response', 'No Obligation', '100% Free'].map(x => (
              <div key={x} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold" />
                <span className="text-cream/90">{x}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-md border border-white/10 bg-ink-panel">
            <div className="text-cream/60 text-xs uppercase tracking-widest">Prefer to talk?</div>
            <a href={`tel:${COMPANY.phoneRaw}`} className="mt-1 font-display uppercase text-3xl text-gold inline-flex items-center gap-2 hover:text-gold-light transition-colors">
              <Phone className="w-6 h-6" /> {COMPANY.phone}
            </a>
            <div className="mt-1 text-cream/50 text-sm">{COMPANY.hours}</div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-md bg-ink-panel border border-white/10 text-cream p-6 md:p-8 shadow-2xl">
            {sent ? (
              <div className="py-8 text-center">
                <CheckCircle2 className="w-14 h-14 mx-auto text-gold" />
                <h3 className="mt-4 font-display uppercase text-3xl text-cream">You&rsquo;re on the list!</h3>
                <p className="mt-2 text-cream/70">We&rsquo;ll call you at {form.phone} within the hour during business hours to schedule your on-site estimate.</p>
                <button className="mt-6 btn-outline" onClick={() => { setSent(false); setForm({ name:'', phone:'', email:'', city:'', service:'', timeline:'', length:'', notes:'' }); }}>
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-1">
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Full name *</Label>
                  <Input value={form.name} onChange={(e) => onChange('name', e.target.value)} placeholder="Jane Boudreaux" className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30" />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Phone *</Label>
                  <Input type="tel" value={form.phone} onChange={(e) => onChange('phone', e.target.value)} placeholder="(318) 555-0134" className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30" />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => onChange('email', e.target.value)} placeholder="you@email.com" className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30" />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">City</Label>
                  <Input value={form.city} onChange={(e) => onChange('city', e.target.value)} placeholder="Shreveport, LA" className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30" />
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Service needed</Label>
                  <Select value={form.service} onValueChange={(v) => onChange('service', v)}>
                    <SelectTrigger className="mt-1.5 bg-ink border-white/15 text-cream"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                    <SelectContent>
                      {FENCE_TYPES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Timeline</Label>
                  <Select value={form.timeline} onValueChange={(v) => onChange('timeline', v)}>
                    <SelectTrigger className="mt-1.5 bg-ink border-white/15 text-cream"><SelectValue placeholder="How soon?" /></SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Approx. length or size (optional)</Label>
                  <Input value={form.length} onChange={(e) => onChange('length', e.target.value)} placeholder="e.g. 180 linear feet, or 300 sqft deck" className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-cream/80 text-xs uppercase tracking-widest">Project details</Label>
                  <Textarea value={form.notes} onChange={(e) => onChange('notes', e.target.value)} placeholder="Tell us about your yard, style preferences, gates, etc." rows={4} className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-4 disabled:opacity-70 disabled:cursor-not-allowed">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4" /> Send my free quote request</>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-cream/50 text-center">By submitting, you agree to be contacted about your project. We never share your info.</p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
